import React, { useState, useEffect, useRef } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { CheckCircle, ErrorOutline } from '@material-ui/icons';
import { StepCard, StepHeader, StepFormContainer } from './PxwStepComponents';
import type { TaskStatus } from './usePxwConnectorConfig';

interface DeployBuildStepProps {
  taskStatuses: Record<string, TaskStatus>;
  taskErrors: Record<string, string>;
  onRunTask: (taskId: string) => Promise<boolean>;
}

const BUILD_DEPLOY_SECONDS = 180;

const DeployBuildStep = ({ taskStatuses, taskErrors, onRunTask }: DeployBuildStepProps) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(BUILD_DEPLOY_SECONDS);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const vanillaStatus = taskStatuses['vanilla'] || 'idle';
  const deployStatus = taskStatuses['deploy-build'] || 'idle';

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const handleStartDeployBuild = () => {
    setIsDeploying(true);
    setTimerSeconds(BUILD_DEPLOY_SECONDS);
    timerRef.current = setInterval(() => {
      setTimerSeconds((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          onRunTask('deploy-build').finally(() => setIsDeploying(false));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTimer = (secs: number) =>
    `${Math.floor(secs / 60).toString().padStart(2, '0')}:${(secs % 60).toString().padStart(2, '0')}`;

  return (
    <StepCard>
      <StepHeader title="Deploy Build" subtitle="Run Vanilla Settings configuration and deploy the gateway build in sequence." />
      <StepFormContainer>
        <div className="small-12">
          <div className="pxw-review-item">
            <div className="pxw-review-main">
              <div>
                <p className="pxw-review-title">1. Update ERP Config with vanilla settings</p>
                <p className="pxw-review-endpoint">POST /api/PxwTenantVanillaSettingSetup</p>
              </div>
              <div>
                {vanillaStatus === 'idle' && (
                  <button type="button" className="pxw-review-btn-run" onClick={() => onRunTask('vanilla')}>Run Vanilla Settings</button>
                )}
                {vanillaStatus === 'loading' && (
                  <span className="pxw-review-status-loading"><CircularProgress size={14} className="pxw-review-spinner" /> Running...</span>
                )}
                {vanillaStatus === 'success' && (
                  <span className="pxw-review-status-success"><CheckCircle className="pxw-review-icon-success" /> Done</span>
                )}
                {vanillaStatus === 'error' && (
                  <button type="button" className="pxw-review-btn-retry" onClick={() => onRunTask('vanilla')}><ErrorOutline className="pxw-review-icon-retry" /> Retry</button>
                )}
              </div>
            </div>
            {vanillaStatus === 'error' && taskErrors['vanilla'] && <div className="pxw-review-error-box">Error: {taskErrors['vanilla']}</div>}
          </div>

          <div className="pxw-review-item">
            <div className="pxw-review-main">
              <div>
                <p className="pxw-review-title">2. Deploy Build (3 min timer)</p>
                <p className="pxw-review-endpoint">POST /api/PxwTenantDeployBuild</p>
              </div>
              <div>
                {isDeploying && timerSeconds > 0 ? (
                  <span className="pxw-review-status-loading"><CircularProgress size={14} className="pxw-review-spinner" /> Deploying build... ({formatTimer(timerSeconds)})</span>
                ) : deployStatus === 'loading' ? (
                  <span className="pxw-review-status-loading"><CircularProgress size={14} className="pxw-review-spinner" /> Running...</span>
                ) : deployStatus === 'success' ? (
                  <span className="pxw-review-status-success"><CheckCircle className="pxw-review-icon-success" /> Done</span>
                ) : (
                  <button
                    type="button"
                    className="pxw-review-btn-run"
                    disabled={vanillaStatus !== 'success' || isDeploying}
                    onClick={handleStartDeployBuild}
                  >
                    {deployStatus === 'error' ? 'Retry Deploy Build' : 'Deploy Build'}
                  </button>
                )}
              </div>
            </div>
            {deployStatus === 'error' && taskErrors['deploy-build'] && <div className="pxw-review-error-box">Error: {taskErrors['deploy-build']}</div>}
          </div>
        </div>
      </StepFormContainer>
    </StepCard>
  );
};

export default DeployBuildStep;
